import React, {useCallback} from 'react';
import {
  TList,
  TRecord,
  TText,
  TTypeConstructor,
} from '@packages/systems/wfdl-ast';
import {
  type ContextualExpression,
  ContextualExpressions,
  stripAnn,
} from '@packages/utilities/contextual-expression';
import {
  withSystem,
  withMeta,
  Plugins,
  type ExpressionPath,
} from '@packages/systems/wfdl';
import {
  ExpressionEditor,
  extendExpressionEditorActionAtPath,
} from '@packages/systems/ExpressionEditor-utils';
import {qualify, typeName} from '@packages/systems/wfdl-Qualified';
import {
  ATTR_MULTIPLE_PATH,
  ATTR_REQUIRED_PATH,
  FORM_DEFAULT_LABEL,
  FORM_OPTS_PATH,
  ATTR_ID_PATH,
} from '../constants';
import {emotionStyled, tokens} from '@packages/utilities/styled';
import {
  useUpdateSiblingLabel,
  useUpdateFormNameDependencies,
} from './formHooks';
import {
  CustomControls,
  type CustomControlsType,
} from '@packages/systems/ExpressionEditor-custom-controls';
import {bulkAddSelectOptionsListActions} from '../settings/BulkAddSelectOptions';
import {createDevLinkRootAlias} from '@packages/utilities/integrated-types';

const {getIn} = ContextualExpressions;

const Grid = emotionStyled('div')({
  display: 'grid',
  gap: tokens.space[2],
});

const fields = {
  form: withSystem(
    {labelVariant: 'hidden'},
    TRecord({
      name: withSystem(
        {label: 'Name', defaultValue: FORM_DEFAULT_LABEL, isLocalizable: true},
        TTypeConstructor(qualify(Plugins.name('Basic'), typeName('OnBlurText')))
      ),
      opts: withMeta(
        {sortable: true},
        withSystem(
          {label: 'Choices', isLocalizable: true},
          TList(
            TTypeConstructor(
              qualify(Plugins.name('Form'), typeName('FormSelectOption'))
            )
          )
        )
      ),
      type: withSystem({defaultValue: 'select', private: true}, TText),
    })
  ),
  attr: TRecord({
    id: withSystem(
      {
        private: true,
        defaultValue: FORM_DEFAULT_LABEL,
        label: 'ID',
        isBindable: false,
        isLocalizable: true,
      },
      TTypeConstructor(qualify(Plugins.name('Basic'), typeName('IdTextInput')))
    ),
    name: withSystem(
      {defaultValue: FORM_DEFAULT_LABEL, isLocalizable: true},
      TText
    ),
    'data-name': withSystem(
      {defaultValue: FORM_DEFAULT_LABEL, private: true, isLocalizable: true},
      TText
    ),
    required: withSystem(
      {
        defaultValue: false,
        label: 'Required',
        labelVariant: 'hidden',
        isLocalizable: true,
      },
      TTypeConstructor(
        qualify(Plugins.name('Basic'), typeName('ReverseLabelBoolean'))
      )
    ),
    multiple: withSystem(
      {
        defaultValue: false,
        label: 'Allow multiple selection',
        labelVariant: 'hidden',
        isLocalizable: true,
      },
      TTypeConstructor(
        qualify(Plugins.name('Basic'), typeName('ReverseLabelBoolean'))
      )
    ),
  }),
} as const;

const getExpressionFromPath = (
  expr: ContextualExpression,
  path: ExpressionPath
) => {
  const expression = getIn(expr, path);
  if (!expression)
    throw Error(`failed to get expression at path: ${JSON.stringify(path)}`);
  return expression;
};

const FormSelectType = createDevLinkRootAlias({
  params: ['extraAtomFields'],
  fields,
  render: function FormSelectRender({
    expr,
    dispatch,
  }: {
    expr: ContextualExpression;
    dispatch: any;
  }) {
    const [formNameExpr, setFormName] = useUpdateFormNameDependencies(
      expr,
      dispatch
    );

    const id =
      ContextualExpressions.getText(
        stripAnn(getExpressionFromPath(expr, ATTR_ID_PATH))
      ) || '';
    useUpdateSiblingLabel(id);
    const dispatchAt = extendExpressionEditorActionAtPath(dispatch);

    const optsExpr = getExpressionFromPath(expr, FORM_OPTS_PATH);
    const optsDispatch = useCallback(dispatchAt(FORM_OPTS_PATH), [dispatchAt]);

    // Adds the bulk add control to the Choices list header without disturbing
    // the controls the Designer already provides (overrides, bindings, etc).
    const parentCustomControls = React.useContext(CustomControls);
    const optsCustomControls: CustomControlsType = React.useMemo(
      () => ({
        ...parentCustomControls,
        ListActionControls: bulkAddSelectOptionsListActions,
      }),
      [parentCustomControls]
    );

    const requiredExpr = getExpressionFromPath(expr, ATTR_REQUIRED_PATH);
    const requiredDispatch = useCallback(dispatchAt(ATTR_REQUIRED_PATH), [
      dispatchAt,
    ]);

    const multipleExpr = getExpressionFromPath(expr, ATTR_MULTIPLE_PATH);
    const multipleDispatch = useCallback(dispatchAt(ATTR_MULTIPLE_PATH), [
      dispatchAt,
    ]);

    return (
      <Grid>
        {/* Name */}
        <ExpressionEditor expression={formNameExpr} dispatch={setFormName} />
        {/* Opts */}
        <CustomControls.Provider value={optsCustomControls}>
          <ExpressionEditor expression={optsExpr} dispatch={optsDispatch} />
        </CustomControls.Provider>
        <div>
          {/* Required */}
          <ExpressionEditor
            expression={requiredExpr}
            dispatch={requiredDispatch}
          />
          {/* Multiple */}
          <ExpressionEditor
            expression={multipleExpr}
            dispatch={multipleDispatch}
          />
        </div>
      </Grid>
    );
  },
});

export default FormSelectType;
